import { Link, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { movies, categories } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'update'>('update');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const filteredMovies: MovieItem[] = useMemo(() => {
    let result = activeCategory === 'all'
      ? [...movies]
      : movies.filter((m) => m.categorySlug === activeCategory || m.categorySlug === activeCategory);

    if (result.length === 0 && activeCategory !== 'all') {
      result = movies.filter((m) => m.categorySlug === activeCategory);
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        result.sort((a, b) => b.year - a.year);
        break;
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    }

    return result;
  }, [activeCategory, sortBy]);

  const categoryName = categories.find((c) => c.slug === activeCategory)?.name || '全部';

  const categoryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `鲁鲁影院 - ${categoryName}`,
    description: `鲁鲁影院${categoryName}高清免费在线观看，海量${categoryName}资源每日更新，高清流畅不缓冲`,
    url: `https://lulucinema.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '鲁鲁影院',
      url: 'https://lulucinema.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`鲁鲁影院-${categoryName}高清免费在线观看_${categoryName}排行榜_高清流畅不缓冲`}
        description={`鲁鲁影院${categoryName}频道提供海量${categoryName}高清免费在线观看，每日更新最新${categoryName}，高清画质流畅播放，无需缓冲，畅享观影体验。`}
        keywords={`鲁鲁影院,${categoryName},高清免费,在线观看,${categoryName}排行榜,免费${categoryName},高清流畅`}
        canonicalUrl={`https://lulucinema.com/category/${activeCategory}`}
        structuredData={categoryStructuredData}
      />

      <div className="min-h-screen bg-background-100">
        <Navbar />

        <main className="pt-16">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          <section className="py-6 md:py-8">
            <div className="container-main">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 mb-6">
                {categoryName}
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  共 {filteredMovies.length} 部
                </span>
              </h1>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <div className="flex items-center gap-2 flex-wrap">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-1.5 text-sm rounded-full transition-all duration-200 whitespace-nowrap ${
                        activeCategory === cat.slug
                          ? 'bg-primary-500 text-background-950 font-medium'
                          : 'bg-background-200/60 text-foreground-400 hover:bg-background-200 hover:text-foreground-300'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="text-xs text-foreground-500 mr-1">排序:</span>
                  {([
                    { key: 'update', label: '最新' },
                    { key: 'rating', label: '评分' },
                    { key: 'year', label: '年份' },
                  ] as const).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSortBy(s.key)}
                      className={`px-3 py-1 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        sortBy === s.key
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-background-200/60 text-foreground-500 hover:bg-background-300'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                    <i className="ri-movie-line text-2xl text-foreground-500 w-8 h-8 flex items-center justify-center"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的影片，请查看其他分类</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}